import { Send } from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { SearchInput } from "@/core/components/search-input";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/core/components/ui/tooltip";
import { multiFieldSearch } from "@/core/utils/multi-field-search";
import { getMissingTechApprovalAccessKeys } from "@/payroll/approvals/utils";
import {
  PAYROLL_EMAILS_ACCESS,
  type PayrollEmailsAccess,
} from "@/payroll/pay-breakdown/types";
import {
  useGetPayPeriodDetail,
  useGetPayPeriods,
} from "@/payroll/pay-periods/query";
import { TeamFilter } from "@/payroll/teams/team-filter";
import { useTeamFilter } from "@/payroll/teams/use-team-filter";
import {
  useGetPayrollStatementPackApprovals,
  usePayrollStatementDeliveryPolling,
  usePayrollStatementNotifications,
  useRetryPayrollStatementProjectionBuild,
} from "./query";
import { SIDEBAR_VIEW, type SidebarView } from "./sidebar-view";
import { TechnicianDetailSidebar } from "./technician-detail-sidebar";
import type { PayrollStatementNotificationRecipient } from "./types";
import { useKnownPackApprovals } from "./use-known-pack-approvals";
import { usePayrollStatementNoteDrafts } from "./use-note-drafts";
import { useRecipientSelection } from "./use-recipient-selection";
import { useSendPayrollStatements } from "./use-send-payroll-statements";
import { useSentPackDriftRefresh } from "./use-sent-pack-drift-refresh";
import {
  formatNotificationPeriod,
  getBlockedChangedPackRecipients,
  getRecipientsWithChangedPacks,
  getSendDisabledReason,
  isRenotifyOnly,
  mergePolledPackApprovals,
  sortPayrollStatementRecipients,
} from "./utils";

const EMPTY_RECIPIENTS: PayrollStatementNotificationRecipient[] = [];

interface PacksSectionInput {
  businessId: string | null;
  periodStart: string | null;
  payrollEmailsAccess: PayrollEmailsAccess;
  /**
   * Technicians with an open PackChat thread about this pack. They are marked and
   * sorted to the top, so a manager can see who is waiting without reading every
   * row. The questions themselves live on PackChat.
   */
  awaitingReplyTechnicianIds?: Set<string>;
}

/**
 * The whole PayPacks controller: queries, selection, send, notes, detail state.
 *
 * Kept as one hook rather than split, because it is one dependency chain —
 * recipients feed the search filter, which feeds the selection, which feeds the
 * send — and splitting it across two components would mean running the same
 * hooks twice.
 */
const usePacksSectionState = ({
  businessId,
  periodStart,
  payrollEmailsAccess,
  awaitingReplyTechnicianIds,
}: PacksSectionInput) => {
  const { teams, teamIds, setTeamIds } = useTeamFilter(
    periodStart ? businessId : null,
  );
  const canUsePayrollEmails =
    payrollEmailsAccess === PAYROLL_EMAILS_ACCESS.enabled;
  const previewOnly = !canUsePayrollEmails;
  const [detailTechnicianId, setDetailTechnicianId] = useState<string | null>(
    null,
  );
  const [sidebarView, setSidebarView] = useState<SidebarView>(
    SIDEBAR_VIEW.preview,
  );
  const [confirmSendOpen, setConfirmSendOpen] = useState(false);
  const [technicianSearch, setTechnicianSearch] = useState("");

  const {
    data,
    isLoading,
    // The approvals memory needs a DELIVERED payload, which neither the data nor its
    // timestamp can report on their own — see useKnownPackApprovals.
    isFetching: isFetchingNotifications,
    dataUpdatedAt: notificationsUpdatedAt,
    errorUpdatedAt: notificationsErroredAt,
    error,
    projectionBuild,
    canRetryProjectionHydration,
    isProjectionRefreshing,
    refetchProjectionBuild,
    refetch: refetchNotifications,
  } = usePayrollStatementNotifications(businessId, periodStart ?? null);
  const { mutate: retryProjectionBuild, isPending: isRetryingProjection } =
    useRetryPayrollStatementProjectionBuild(businessId, periodStart ?? null);
  const { data: payPeriods = [] } = useGetPayPeriods(businessId);
  // Approval status lives on the period SHOW endpoint only, so the "not yet
  // ready for PayPacks" warning needs the detail for this period, since the
  // index row we matched by start date does not carry it.
  const selectedPeriod = payPeriods.find(
    (period) => period.start_date === periodStart,
  );
  const periodId = selectedPeriod?.id;
  const { data: periodDetail } = useGetPayPeriodDetail(businessId, periodId, {
    runId: selectedPeriod?.latest_completed_ai_script_run_id,
  });
  // Approvals have their own polling query. Merge them onto the recipients here
  // so every reader below (the column, the header count, the sidebar) sees one shape
  // and does not need to know where the fresher answer came from.
  // `dataUpdatedAt` rides along for useSentPackDriftRefresh: it moves on every
  // successful poll even when the payload is byte-identical, which structural sharing
  // means the data reference does not.
  const { data: freshApprovals, dataUpdatedAt: approvalsUpdatedAt } =
    useGetPayrollStatementPackApprovals(
      businessId,
      periodStart,
      !!data && !previewOnly,
    );
  // The comparisons the polls have actually made, so a poll that cannot compare falls
  // back to the latest answer rather than to the one the page loaded with.
  const knownApprovals = useKnownPackApprovals({
    recipients: data?.recipients,
    polled: freshApprovals,
    payloadFetching: isFetchingNotifications,
    payloadUpdatedAt: notificationsUpdatedAt,
    payloadErroredAt: notificationsErroredAt,
  });
  const recipients = useMemo(
    () =>
      mergePolledPackApprovals(
        sortPayrollStatementRecipients(
          data?.recipients ?? EMPTY_RECIPIENTS,
          awaitingReplyTechnicianIds,
        ),
        freshApprovals,
        knownApprovals,
      ),
    [
      awaitingReplyTechnicianIds,
      data?.recipients,
      freshApprovals,
      knownApprovals,
    ],
  );
  const filteredRecipients = useMemo(() => {
    const teamRecipients =
      teamIds.length === 0
        ? recipients
        : recipients.filter((recipient) =>
            recipient.team_access_key_ids?.some((id) => teamIds.includes(id)),
          );
    return multiFieldSearch(teamRecipients, technicianSearch, ["name"]);
  }, [recipients, teamIds, technicianSearch]);
  const {
    selectedRecipientIds,
    eligibleRecipients,
    selectedEligibleRecipients,
    allEligibleSelected,
    someEligibleSelected,
    toggleRecipient,
    toggleAllEligible,
    selectOnly,
  } = useRecipientSelection({
    recipients: filteredRecipients,
    selectionKey: `${businessId ?? ""}:${periodStart ?? ""}`,
    enabled: !!data && !previewOnly,
  });
  const { noteDrafts, setNoteDraft, trackSubmittedNotes } =
    usePayrollStatementNoteDrafts(recipients);
  // Refresh the full payload when approval polling detects a send from another
  // tab or manager. See useSentPackDriftRefresh for the retry rule.
  useSentPackDriftRefresh({
    recipients,
    states: freshApprovals?.sent_pack_states,
    refetch: refetchNotifications,
    pollUpdatedAt: approvalsUpdatedAt,
    enabled: !!data && !previewOnly,
  });
  const changedRecipients = useMemo(
    () => getRecipientsWithChangedPacks(filteredRecipients),
    [filteredRecipients],
  );
  const blockedChangedRecipients = useMemo(
    () => getBlockedChangedPackRecipients(filteredRecipients),
    [filteredRecipients],
  );
  const selectChangedPacks = useCallback(() => {
    selectOnly(changedRecipients);
  }, [changedRecipients, selectOnly]);
  const detailRecipient =
    filteredRecipients.find(
      (recipient) => recipient.technician_id === detailTechnicianId,
    ) ?? null;
  const periodLabel = formatNotificationPeriod({
    startDate: data?.period?.start_date,
    endDate: data?.period?.end_date,
    fallback: periodStart ?? "",
  });
  const sendDisabledReason = getSendDisabledReason({
    canSend: data?.can_send ?? false,
    cannotSendReason: data?.cannot_send_reason,
    selectedEligibleCount: selectedEligibleRecipients.length,
  });
  const {
    send,
    sendFailures,
    activeSendResponse,
    isSending,
    isSavingNote,
    isSubmitting: isSendSubmitting,
  } = useSendPayrollStatements({
    businessId,
    periodStart,
    previewOnly,
    sendDisabledReason,
    selectedRecipients: selectedEligibleRecipients,
    noteDrafts,
    trackSubmittedNotes,
  });
  const deliveryPoll = usePayrollStatementDeliveryPolling(
    businessId,
    periodStart ?? null,
    activeSendResponse,
  );
  const missingReadyForEmailAccessKeys = getMissingTechApprovalAccessKeys(
    periodDetail?.approval_status,
  );
  const isProjectionBuilding =
    data?.projection_build?.status !== "ready" &&
    projectionBuild?.status === "pending" &&
    !projectionBuild.polling_timed_out &&
    !canRetryProjectionHydration;
  const canShowRecipients =
    !error && (!projectionBuild || projectionBuild.status === "ready");
  const isPayPacksLoading = isLoading || isProjectionBuilding;
  const showRecipientContent = !isPayPacksLoading && canShowRecipients;

  const closeDetail = useCallback(() => setDetailTechnicianId(null), []);

  const openDetail = (
    recipient: PayrollStatementNotificationRecipient,
    view: SidebarView,
  ) => {
    setDetailTechnicianId(recipient.technician_id);
    setSidebarView(view);
  };
  return {
    teams,
    teamIds,
    setTeamIds,
    awaitingReplyTechnicianIds,
    canUsePayrollEmails,
    previewOnly,
    data,
    isLoading,
    error,
    projectionBuild,
    canRetryProjectionHydration,
    isProjectionRefreshing,
    refetchProjectionBuild,
    retryProjectionBuild,
    isRetryingProjection,
    recipients,
    filteredRecipients,
    selectedRecipientIds,
    eligibleRecipients,
    selectedEligibleRecipients,
    allEligibleSelected,
    someEligibleSelected,
    toggleRecipient,
    toggleAllEligible,
    changedRecipients,
    blockedChangedRecipients,
    selectChangedPacks,
    noteDrafts,
    setNoteDraft,
    detailTechnicianId,
    detailRecipient,
    sidebarView,
    setSidebarView,
    openDetail,
    closeDetail,
    technicianSearch,
    setTechnicianSearch,
    periodLabel,
    sendDisabledReason,
    send,
    sendFailures,
    isSending,
    isSavingNote,
    isSendSubmitting,
    deliveryPoll,
    missingReadyForEmailAccessKeys,
    isProjectionBuilding,
    canShowRecipients,
    isPayPacksLoading,
    showRecipientContent,
    confirmSendOpen,
    setConfirmSendOpen,
    businessId,
    periodStart: periodStart ?? null,
  };
};

type PacksSectionValue = ReturnType<typeof usePacksSectionState>;

const PacksSectionContext = createContext<PacksSectionValue | null>(null);

/**
 * Puts the PayPacks controller above the page header.
 *
 * The section's controls — technician search and Publish PayPacks — belong beside the
 * period picker, but the header renders as a sibling far above the recipients
 * table in the same page. That used to be bridged by portaling the rendered
 * controls into a DOM node the header exposed, which put the React tree out of
 * step with the DOM: events bubbled to the wrong parent, the target could be null
 * with a silent fallback, and tests needed a live node to render at all. Holding
 * the state above both readers instead makes it ordinary React.
 *
 * Pass a null `periodStart` to idle it: every query underneath is gated on
 * business and period, so it can stay mounted on other tabs without fetching. It
 * has to stay mounted — swapping it per active section would change the element
 * type at the top of the page and remount the Grid, which is kept alive on
 * purpose so a workbook and its selection survive tab switches.
 */
export const PacksSectionProvider = ({
  children,
  ...input
}: PacksSectionInput & { children: ReactNode }) => {
  const value = usePacksSectionState(input);

  return (
    <PacksSectionContext.Provider value={value}>
      {children}
    </PacksSectionContext.Provider>
  );
};

export const usePacksSection = () => {
  const section = useContext(PacksSectionContext);
  if (!section) {
    throw new Error(
      "PayPacks components must be rendered inside a PacksSectionProvider.",
    );
  }
  return section;
};

/**
 * The PayPacks controls, for the page header's section slot.
 *
 * Keep the team filter mounted while recipients load or become empty.
 * Other actions require recipients.
 */
export const PacksHeaderActions = () => {
  const {
    teams,
    teamIds,
    setTeamIds,
    recipients,
    technicianSearch,
    setTechnicianSearch,
    previewOnly,
    sendDisabledReason,
    isSendSubmitting,
    setConfirmSendOpen,
    selectedEligibleRecipients,
  } = usePacksSection();

  const sendButton = (
    <Button
      size="sm"
      leftIcon={<Send className="h-4 w-4" />}
      loading={isSendSubmitting}
      disabled={previewOnly || !!sendDisabledReason}
      title={!previewOnly ? (sendDisabledReason ?? undefined) : undefined}
      onClick={() => setConfirmSendOpen(true)}
    >
      {isRenotifyOnly(selectedEligibleRecipients)
        ? "Renotify"
        : "Publish PayPacks"}
    </Button>
  );

  return (
    <Stack
      direction="row"
      align="center"
      justify="end"
      gap="sm"
      className="w-full flex-wrap sm:w-auto sm:flex-nowrap"
    >
      <TeamFilter teams={teams} value={teamIds} onChange={setTeamIds} />
      {recipients.length > 0 && (
        <>
          {/* align="stretch" is load-bearing: Stack defaults to items-start, which
          left the search field at its intrinsic width inside this sized
          wrapper and opened a ~30px hole before the send button. */}
          <Stack
            gap="none"
            align="stretch"
            className="min-w-48 flex-1 sm:w-64 sm:flex-none"
          >
            <SearchInput
              value={technicianSearch}
              onChange={setTechnicianSearch}
              placeholder="Search technicians"
              aria-label="Search PayPacks by technician name"
              className="w-full bg-white"
            />
          </Stack>
          {previewOnly ? (
            <Tooltip>
              <TooltipTrigger asDiv>{sendButton}</TooltipTrigger>
              <TooltipContent>
                Complete PayPack email setup through Volca to start sending
                emails.
              </TooltipContent>
            </Tooltip>
          ) : (
            sendButton
          )}
        </>
      )}
    </Stack>
  );
};

/**
 * The technician detail sidebar, for the page's sidebar slot.
 *
 * The page owns the shared sidebar's open state, and it renders this directly, so
 * the two are wired with props rather than a context or a portal.
 */
export const PacksSidebar = ({
  onOpenChange,
  registerClose,
}: {
  onOpenChange?: (open: boolean) => void;
  registerClose?: (close: (() => void) | null) => void;
} = {}) => {
  const {
    businessId,
    periodStart,
    detailRecipient,
    detailTechnicianId,
    sidebarView,
    setSidebarView,
    closeDetail,
    noteDrafts,
    setNoteDraft,
    previewOnly,
    projectionBuild,
  } = usePacksSection();

  useEffect(() => {
    registerClose?.(closeDetail);

    return () => registerClose?.(null);
  }, [closeDetail, registerClose]);

  useEffect(() => {
    onOpenChange?.(!!detailRecipient);

    return () => onOpenChange?.(false);
  }, [detailRecipient, onOpenChange]);

  return (
    <TechnicianDetailSidebar
      businessId={businessId}
      periodStart={periodStart ?? null}
      recipient={detailRecipient}
      view={sidebarView}
      onViewChange={setSidebarView}
      onClose={closeDetail}
      noteDraft={
        detailRecipient
          ? (noteDrafts[detailRecipient.technician_id] ??
            detailRecipient.note ??
            "")
          : ""
      }
      onNoteChange={(value) => {
        if (!detailTechnicianId) return;
        setNoteDraft(detailTechnicianId, value);
      }}
      previewOnly={previewOnly}
      projectionBuild={projectionBuild}
    />
  );
};
